import React, { useState, useEffect, KeyboardEvent } from 'react';
import { Save, ArrowLeft, Plus, Upload, Trash2, ExternalLink, X, CreditCard as Edit2, Check, Database } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import {
  draftsToParticipantLines,
  newParticipantDraft,
  participantLinesToDrafts,
  type ParticipantDraft,
} from '../../lib/transcriptParticipants';

interface Transcript {
  id: string;
  title: string;
  content: string;
  summary: string;
  meeting_date: string;
  participants: string[];
  tags: string[];
  created_at: string;
  updated_at: string;
  /** RAG: none | pending | ready | error — does not change transcript body text */
  transcript_chunks_status?: string;
  transcript_chunks_indexed_at?: string | null;
  /** Read.ai session id when ingested via webhook */
  transcript_id?: string | null;
}

interface ContentBlock {
  id: string;
  name: string;
  content: string;
  tags: string[];
  created_at: string;
  is_published: boolean;
  published_at: string | null;
}

interface TranscriptModalProps {
  isOpen: boolean;
  onClose: () => void;
  transcript: Transcript | null;
  onSuccess: () => void;
}

interface EditableBlockState {
  id: string | null;
  name: string;
  content: string;
}

type Tab = 'details' | 'blocks' | 'attendees';

const TranscriptModal: React.FC<TranscriptModalProps> = ({
  isOpen,
  onClose,
  transcript,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<Partial<Transcript>>({
    title: '',
    content: '',
    summary: '',
    meeting_date: new Date().toISOString().split('T')[0],
    tags: [],
  });
  const [tagInput, setTagInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('details');
  const [contentBlocks, setContentBlocks] = useState<ContentBlock[]>([]);
  const [isLoadingBlocks, setIsLoadingBlocks] = useState(false);
  const [editingBlock, setEditingBlock] = useState<EditableBlockState | null>(null);
  const [isUpdatingBlock, setIsUpdatingBlock] = useState(false);
  const [indexChunksLoading, setIndexChunksLoading] = useState(false);
  const [indexMeta, setIndexMeta] = useState<{ status: string; at: string | null }>({ status: 'none', at: null });
  const [participantRows, setParticipantRows] = useState<ParticipantDraft[]>([]);
  const isNewTranscript = !transcript;
  const isReadAiMeeting = Boolean(transcript?.transcript_id?.trim());

  useEffect(() => {
    if (transcript) {
      setFormData({
        ...transcript,
        meeting_date: new Date(transcript.meeting_date).toISOString().split('T')[0],
      });
      setParticipantRows(participantLinesToDrafts(transcript.participants));
      fetchContentBlocks(transcript.id);
    } else {
      setFormData({
        title: '',
        content: '',
        summary: '',
        meeting_date: new Date().toISOString().split('T')[0],
        tags: [],
      });
      setParticipantRows([]);
      setContentBlocks([]);
    }
    setActiveTab('details');
    if (transcript?.id) {
      setIndexMeta({
        status: transcript.transcript_chunks_status ?? 'none',
        at: transcript.transcript_chunks_indexed_at ?? null,
      });
    } else {
      setIndexMeta({ status: 'none', at: null });
    }
  }, [transcript]);

  const fetchContentBlocks = async (transcriptId: string) => {
    setIsLoadingBlocks(true);
    try {
      const { data, error } = await supabase
        .from('content_blocks')
        .select('*')
        .eq('transcript_id', transcriptId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setContentBlocks(data || []);
    } catch (error: any) {
      toast.error(`Error fetching content blocks: ${error.message}`);
    } finally {
      setIsLoadingBlocks(false);
    }
  };

  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        if (showDeleteConfirm) {
          setShowDeleteConfirm(false);
        } else {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleEscKey as any);
    return () => {
      document.removeEventListener('keydown', handleEscKey as any);
    };
  }, [isOpen, onClose, showDeleteConfirm]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      setFormData(prev => ({
        ...prev,
        title: file.name.replace(/\.[^/.]+$/, ''),
        content: text,
      }));
      toast.success('File content loaded successfully');
    } catch (error: any) {
      toast.error(`Error reading file: ${error.message}`);
    }
  };

  const handleTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags?.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tag],
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags?.filter(t => t !== tagToRemove) || [],
    }));
  };

  const handleDelete = async () => {
    if (!transcript) return;
    
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('transcripts')
        .delete()
        .eq('id', transcript.id);

      if (error) throw error;

      toast.success('Transcript deleted successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(`Error deleting transcript: ${error.message}`);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleIndexChunks = async () => {
    if (!transcript?.id) return;
    setIndexChunksLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke<{
        ok?: boolean;
        chunkCount?: number;
        error?: string;
      }>('index-transcript-chunks', { body: { transcript_id: transcript.id } });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      toast.success(`Indexed ${data?.chunkCount ?? 0} chunk(s) for search`);
      setIndexMeta({ status: 'ready', at: new Date().toISOString() });
      onSuccess();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Index failed';
      toast.error(msg);
      setIndexMeta((m) => ({ ...m, status: 'error' }));
    } finally {
      setIndexChunksLoading(false);
    }
  };

  const removeParticipantRow = (key: string) => {
    setParticipantRows((rows) => rows.filter((row) => row.key !== key));
  };

  const updateParticipantRow = (key: string, patch: Partial<Pick<ParticipantDraft, 'name' | 'email'>>) => {
    setParticipantRows((rows) =>
      rows.map((row) => (row.key === key ? { ...row, ...patch } : row)),
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSaving(true);

    try {
      const dataToSubmit: Record<string, unknown> = {
        ...formData,
        meeting_date: new Date(formData.meeting_date + 'T00:00:00').toISOString(),
        participants: draftsToParticipantLines(participantRows),
      };
      delete dataToSubmit.transcript_chunks_status;
      delete dataToSubmit.transcript_chunks_indexed_at;
      if (!isNewTranscript && transcript && formData.content !== transcript.content) {
        dataToSubmit.transcript_chunks_status = 'none';
        dataToSubmit.transcript_chunks_indexed_at = null;
        setIndexMeta({ status: 'none', at: null });
      }

      let result;
      if (isNewTranscript) {
        result = await supabase.from('transcripts').insert(dataToSubmit).select();
      } else {
        result = await supabase.from('transcripts').update(dataToSubmit).eq('id', transcript.id).select();
      }

      if (result.error) throw result.error;

      toast.success(isNewTranscript ? 'Transcript created successfully' : 'Transcript updated successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleOpenExtractor = () => {
    if (!transcript?.id) return;
    window.open(`https://lccto-content-extractor.netlify.app/?transcript_id=${transcript.id}`, '_blank');
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleContentTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addContentTag();
    }
  };

  const addContentTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags?.includes(tag)) {
      setEditingBlock(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tag],
      }));
      setTagInput('');
    }
  };

  const removeContentTag = (tagToRemove: string) => {
    setEditingBlock(prev => ({
      ...prev,
      tags: prev.tags?.filter(t => t !== tagToRemove) || [],
    }));
  };
  
  const handleEditBlock = (block: ContentBlock) => {
    console.log('b', block)
    setEditingBlock({
      id: block.id,
      name: block.name,
      content: block.content,
      tags: block.tags || []
    });
  };

  const handleCancelEdit = () => {
    setEditingBlock(null);
  };

  
  const handleSaveBlock = async () => {
    if (!editingBlock?.id) return;

    setIsUpdatingBlock(true);
    try {
      const { error } = await supabase
        .from('content_blocks')
        .update({
          name: editingBlock.name,
          content: editingBlock.content,
          tags: editingBlock.tags || []
        })
        .eq('id', editingBlock.id);

      if (error) throw error;

      await fetchContentBlocks(transcript!.id);
      setEditingBlock(null);
      toast.success('Content block updated successfully');
    } catch (error: any) {
      toast.error(`Error updating content block: ${error.message}`);
    } finally {
      setIsUpdatingBlock(false);
    }
  };

  const formatRelativeTime = (date: string) => {
    const now = new Date();
    const updateDate = new Date(date);
    const diffInHours = Math.floor((now.getTime() - updateDate.getTime()) / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);
    const diffInMonths = Math.floor(diffInDays / 30);

    let relativeTime = '';
    if (diffInMonths > 0) {
      relativeTime = `${diffInMonths} ${diffInMonths === 1 ? 'month' : 'months'} ago`;
    } else if (diffInDays > 0) {
      relativeTime = `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
    } else if (diffInHours > 0) {
      relativeTime = `${diffInHours} ${diffInHours === 1 ? 'hr' : 'hrs'} ago`;
    } else {
      relativeTime = 'just now';
    }

    return `${updateDate.toLocaleDateString()} (${relativeTime})`;
  };
  
  const renderContentBlock = (block: ContentBlock) => {
    const isEditing = editingBlock?.id === block.id;

    if (isEditing) {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border-2 border-emerald-500 dark:border-emerald-400">
          <div className="space-y-3">
            <input
              type="text"
              value={editingBlock.name}
              onChange={(e) => setEditingBlock({ ...editingBlock, name: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
              placeholder="Block name"
            />
            <textarea
              value={editingBlock.content}
              onChange={(e) => setEditingBlock({ ...editingBlock, content: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
              placeholder="Block content"
            />

<div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Tags
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {Array.isArray(editingBlock.tags) && editingBlock.tags.map((tag: string) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeContentTag(tag)}
                      className="ml-2 focus:outline-none"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleContentTagKeyDown}
                  placeholder="Add a tag..."
                  className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 py-2.5 px-3"
                />
                <button
                  type="button"
                  onClick={addContentTag}
                  className="px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Press Enter or click the plus button to add a tag
              </p>
            </div>
            
            <div className="flex justify-end gap-2">
              <button
                onClick={handleCancelEdit}
                className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveBlock}
                disabled={isUpdatingBlock}
                className="flex items-center px-3 py-1.5 text-sm bg-emerald-500 hover:bg-emerald-600 text-white rounded-md transition-colors disabled:opacity-50"
              >
                {isUpdatingBlock ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Check className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="group bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-medium text-gray-900 dark:text-white">
            {block.name}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleEditBlock(block)}
              className="opacity-0 group-hover:opacity-100 p-1 text-gray-500 hover:text-emerald-500 transition-all"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            {block.is_published && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">
                Published
              </span>
            )}
          </div>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2 mb-3">
          {block.content}
        </p>
        {block.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {block.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
        <div className="text-xs text-gray-500 dark:text-gray-400">
          { formatRelativeTime(block.created_at)}
        </div>
      </div>
    );
  };

  const renderAttendeesEditor = () => (
    <div>
      <div className="flex items-center justify-between gap-2 mb-1">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Attendees</h3>
        <button
          type="button"
          onClick={() => setParticipantRows((rows) => [...rows, newParticipantDraft()])}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <Plus className="w-3.5 h-3.5" />
          Add attendee
        </button>
      </div>
      {isReadAiMeeting ? (
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          Read.ai meeting — fix name or email here so subscriber profiles link to this transcript.
        </p>
      ) : (
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          Stored as name + email. Subscriber linking matches on the email address.
        </p>
      )}
      {participantRows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 italic">No attendees yet.</p>
      ) : (
        <div className="space-y-2">
          {participantRows.map((row) => (
            <div
              key={row.key}
              className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_auto] gap-2 items-start"
            >
              <input
                type="text"
                value={row.name}
                onChange={(e) => updateParticipantRow(row.key, { name: e.target.value })}
                placeholder="Name"
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 py-2 px-3 text-sm"
              />
              <input
                type="email"
                value={row.email}
                onChange={(e) => updateParticipantRow(row.key, { email: e.target.value })}
                placeholder="email@domain.com"
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 py-2 px-3 text-sm"
              />
              <button
                type="button"
                onClick={() => removeParticipantRow(row.key)}
                className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                aria-label="Remove attendee"
                title="Remove attendee"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div 
      className={`fixed inset-y-0 right-0 w-full md:w-1/2 bg-white dark:bg-gray-800 shadow-xl transform transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      } z-50`}
    >
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <button
              onClick={onClose}
              className="mr-4 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-semibold">
              {isNewTranscript ? 'Create New Transcript' : 'Edit Transcript'}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {!isNewTranscript && (
              <button
                onClick={handleOpenExtractor}
                className="flex items-center px-3 py-2 text-sm bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Extract
              </button>
            )}
            <label className="flex items-center px-3 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors cursor-pointer">
              <Upload className="w-4 h-4 mr-2" />
              Load File
              <input
                type="file"
                className="hidden"
                accept=".txt,.md,.doc,.docx"
                onChange={handleFileUpload}
              />
            </label>
          </div>
        </div>

        {!isNewTranscript && (
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setActiveTab('details')}
              className={`px-6 py-3 text-sm font-medium transition-colors relative ${
                activeTab === 'details'
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Details
              {activeTab === 'details' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500"></div>
              )}
            </button>
            <button
              onClick={() => setActiveTab('blocks')}
              className={`px-6 py-3 text-sm font-medium transition-colors relative ${
                activeTab === 'blocks'
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Content Blocks
              {activeTab === 'blocks' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500"></div>
              )}
            </button>
            <button
              onClick={() => setActiveTab('attendees')}
              className={`px-6 py-3 text-sm font-medium transition-colors relative ${
                activeTab === 'attendees'
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Attendees
              {activeTab === 'attendees' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500"></div>
              )}
            </button>
          </div>
        )}

        {activeTab === 'details' ? (
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 py-2.5 px-3"
                  placeholder="Enter transcript title..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Meeting Date
                </label>
                <input
                  type="date"
                  value={formData.meeting_date}
                  onChange={(e) => setFormData({ ...formData, meeting_date: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 py-2.5 px-3"
                  required
                />
              </div>

              {isNewTranscript ? <div>{renderAttendeesEditor()}</div> : null}

              {!isNewTranscript && (
                <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/60 p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Q&amp;A search index</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        Status:{' '}
                        <span className="font-medium capitalize">{indexMeta.status}</span>
                        {indexMeta.at && indexMeta.status === 'ready'
                          ? ` · last indexed ${new Date(indexMeta.at).toLocaleString()}`
                          : null}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        Writes chunk rows + vectors only — does not change transcript text.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => void handleIndexChunks()}
                      disabled={indexChunksLoading}
                      className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-emerald-600 bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      <Database className="h-4 w-4" />
                      {indexChunksLoading ? 'Indexing…' : 'Build / refresh index'}
                    </button>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Content
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows={8}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 py-2.5 px-3"
                  placeholder="Enter transcript content..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Summary
                </label>
                <textarea
                  value={formData.summary}
                  onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 py-2.5 px-3"
                  placeholder="Enter a brief summary of the transcript..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tags
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.tags?.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-2 focus:outline-none"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagKeyDown}
                    placeholder="Add a tag..."
                    className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 py-2.5 px-3"
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {!isNewTranscript && !showDeleteConfirm && (
                <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 pt-4 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="flex items-center justify-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Transcript
                  </button>
                </div>
              )}

              {showDeleteConfirm && (
                <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 pt-4 mt-6">
                  <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                    <p className="text-sm text-red-600 dark:text-red-400 mb-4">
                      Are you sure you want to delete this transcript? This action cannot be undone.
                    </p>
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setShowDeleteConfirm(false)}
                        className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleDelete}
                        disabled={isDeleting}
                        className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
                      >
                        {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </form>
        ) : activeTab === 'attendees' ? (
          <div className="flex-1 overflow-y-auto p-6">{renderAttendeesEditor()}</div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6">
            {isLoadingBlocks ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
              </div>
            ) : contentBlocks.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 dark:text-gray-400">
                  No content blocks have been created from this transcript yet.
                </p>
                <button
                  onClick={handleOpenExtractor}
                  className="mt-4 inline-flex items-center px-4 py-2 text-sm bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Extract Content
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {contentBlocks.map((block) => renderContentBlock(block))}
              </div>
            )}
          </div>
        )}

        {(activeTab === 'details' || activeTab === 'attendees') && (
          <div className="shrink-0 p-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSaving || showDeleteConfirm}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg py-2.5 px-4 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  {isNewTranscript ? 'Create Transcript' : 'Update Transcript'}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TranscriptModal;